'use client'

import { useState, useMemo, useCallback } from 'react'
import { formatPrice, formatChange } from '@/lib/formatters'

interface DayRow {
  date: string
  timestamp: number
  price: number
  change: number
  volume: number
  year: number
  month: number
}

interface Props {
  rows: DayRow[]
  years: number[]
  coinName?: string
}

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

function formatVolume(v: number): string {
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`
  return `$${v.toFixed(0)}`
}

function heatBg(change: number): string {
  const abs = Math.abs(change)
  if (change >= 0) {
    if (abs >= 4) return 'rgba(34,197,94,0.10)'
    if (abs >= 2) return 'rgba(34,197,94,0.06)'
    return 'rgba(34,197,94,0.03)'
  } else {
    if (abs >= 4) return 'rgba(239,68,68,0.10)'
    if (abs >= 2) return 'rgba(239,68,68,0.06)'
    return 'rgba(239,68,68,0.03)'
  }
}

export function HistoricalTable({ rows, years, coinName }: Props) {
  const [activeYear, setActiveYear] = useState(years[0])
  const [activeMonth, setActiveMonth] = useState(() => new Date().getMonth())

  const yearRows = useMemo(() => rows.filter((r) => r.year === activeYear), [rows, activeYear])

  const availableMonths = useMemo(() => {
    const set = new Set(yearRows.map((r) => r.month))
    return Array.from(set).sort((a, b) => a - b)
  }, [yearRows])

  const effectiveMonth = availableMonths.includes(activeMonth)
    ? activeMonth
    : availableMonths[availableMonths.length - 1] ?? 0

  // Daily rows newest first (for table display)
  const monthRows = useMemo(
    () => yearRows.filter((r) => r.month === effectiveMonth).reverse(),
    [yearRows, effectiveMonth]
  )

  // Chronological rows (oldest first — for bars chart + CSV export)
  const chronoRows = useMemo(() => [...monthRows].reverse(), [monthRows])

  // Month summary — use chronological so first/last are correct
  const stats = useMemo(() => {
    if (chronoRows.length === 0) return null
    const prices = chronoRows.map((r) => r.price)
    const high = Math.max(...prices)
    const low = Math.min(...prices)
    const avg = prices.reduce((a, b) => a + b, 0) / prices.length
    const first = chronoRows[0].price
    const last = chronoRows[chronoRows.length - 1].price
    const change = first > 0 ? ((last - first) / first) * 100 : 0
    return { high, low, avg, change }
  }, [chronoRows])

  // Max absolute change for inline bar scaling
  const maxAbs = useMemo(
    () => Math.max(...monthRows.map((r) => Math.abs(r.change)), 0.01),
    [monthRows]
  )

  // High/low price day timestamps
  const highTs = useMemo(() => {
    if (monthRows.length === 0) return 0
    return monthRows.reduce((best, r) => (r.price > best.price ? r : best), monthRows[0]).timestamp
  }, [monthRows])

  const lowTs = useMemo(() => {
    if (monthRows.length === 0) return 0
    return monthRows.reduce((best, r) => (r.price < best.price ? r : best), monthRows[0]).timestamp
  }, [monthRows])

  // Change bars data (chronological)
  const barsData = useMemo(() => {
    if (chronoRows.length < 2) return null
    const maxAbsChange = Math.max(...chronoRows.map((r) => Math.abs(r.change)), 0.01)
    const highIdx = chronoRows.reduce((bi, r, i) => (r.price > chronoRows[bi].price ? i : bi), 0)
    const lowIdx = chronoRows.reduce((bi, r, i) => (r.price < chronoRows[bi].price ? i : bi), 0)
    return { maxAbsChange, highIdx, lowIdx }
  }, [chronoRows])

  // CSV export — uses chronoRows (oldest→newest), columns match table exactly
  const exportCsv = useCallback(() => {
    const header = 'Date,Price (USD),Change (%),Volume (USD)\n'
    const csvRows = chronoRows.map((r) =>
      [
        `"${r.date}"`,
        r.price.toFixed(2),
        `${r.change >= 0 ? '+' : ''}${r.change.toFixed(2)}%`,
        Math.round(r.volume).toString(),
      ].join(',')
    )
    const csv = header + csvRows.join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${(coinName || 'crypto').toLowerCase().replace(/\s+/g, '-')}-${MONTH_LABELS[effectiveMonth].toLowerCase()}-${activeYear}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }, [chronoRows, coinName, effectiveMonth, activeYear])

  return (
    <div>
      {/* Year pills */}
      <div className="flex items-center gap-0 border border-zinc-800 w-fit mb-3 max-w-full overflow-x-auto">
        {years.map((y) => (
          <button
            key={y}
            onClick={() => setActiveYear(y)}
            className={`px-4 py-2 text-xs font-medium transition-colors border-r border-zinc-800 last:border-r-0 whitespace-nowrap tabular-nums ${
              activeYear === y
                ? 'bg-zinc-800 text-white'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-900'
            }`}
          >
            {y}
          </button>
        ))}
      </div>

      {/* Month pills */}
      <div className="flex items-center gap-0 border border-zinc-800 w-fit mb-5 max-w-full overflow-x-auto">
        {MONTH_LABELS.map((label, mi) => {
          const hasData = availableMonths.includes(mi)
          const isActive = effectiveMonth === mi
          return (
            <button
              key={mi}
              onClick={() => hasData && setActiveMonth(mi)}
              disabled={!hasData}
              className={`px-3 py-2 text-xs font-medium transition-colors border-r border-zinc-800 last:border-r-0 whitespace-nowrap ${
                isActive
                  ? 'bg-zinc-800 text-white'
                  : hasData
                    ? 'text-zinc-400 hover:text-white hover:bg-zinc-900'
                    : 'text-zinc-700 cursor-not-allowed'
              }`}
            >
              {label}
            </button>
          )
        })}
      </div>

      {/* Month summary */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800 mb-px">
          <div className="bg-black px-4 py-3">
            <p className="text-zinc-400 text-xs mb-0.5">Month change</p>
            <p
              className={`text-sm font-medium tabular-nums ${
                stats.change >= 0 ? 'text-green-500' : 'text-red-500'
              }`}
            >
              {formatChange(stats.change)}
            </p>
          </div>
          <div className="bg-black px-4 py-3">
            <p className="text-zinc-400 text-xs mb-0.5">High</p>
            <p className="text-white text-sm font-medium tabular-nums">{formatPrice(stats.high)}</p>
          </div>
          <div className="bg-black px-4 py-3">
            <p className="text-zinc-400 text-xs mb-0.5">Low</p>
            <p className="text-white text-sm font-medium tabular-nums">{formatPrice(stats.low)}</p>
          </div>
          <div className="bg-black px-4 py-3">
            <p className="text-zinc-400 text-xs mb-0.5">Average</p>
            <p className="text-white text-sm font-medium tabular-nums">{formatPrice(stats.avg)}</p>
          </div>
        </div>
      )}

      {/* Daily change bars */}
      {barsData && (
        <div className="border border-zinc-800 border-t-0 px-3 pt-3 pb-2 mb-5 overflow-hidden">
          <div className="relative h-[112px] flex items-center">
            {/* Zero line */}
            <div className="absolute left-0 right-0 top-1/2 h-px bg-zinc-800" />
            <div className="flex items-stretch gap-px w-full h-full relative z-10">
              {(() => {
                // Total slots = days in the month
                const daysInMonth = new Date(activeYear, effectiveMonth + 1, 0).getDate()
                const slots = Array.from({ length: daysInMonth }, (_, si) => {
                  const row = chronoRows.find((r) => {
                    const d = new Date(r.timestamp)
                    return d.getDate() === si + 1
                  })
                  return { dayNum: si + 1, row }
                })

                return slots.map((slot) => {
                  if (!slot.row) {
                    // Empty day — no data yet
                    return <div key={slot.dayNum} className="flex-1 relative" />
                  }

                  const r = slot.row
                  const i = chronoRows.indexOf(r)
                  const barH = (Math.abs(r.change) / barsData.maxAbsChange) * 36
                  const isHigh = i === barsData.highIdx
                  const isLow = i === barsData.lowIdx
                  const isPos = r.change >= 0
                  const isMarked = isHigh || isLow

                  // Edge alignment based on position in full month
                  const isNearStart = slot.dayNum <= 3
                  const isNearEnd = slot.dayNum >= daysInMonth - 2
                  const align = isNearStart
                    ? 'left-0 items-start'
                    : isNearEnd
                      ? 'right-0 items-end'
                      : 'left-1/2 -translate-x-1/2 items-center'

                  return (
                    <div key={slot.dayNum} className="flex-1 relative">
                      {isPos ? (
                        <div
                          className="absolute left-[15%] right-[15%]"
                          style={{
                            bottom: '50%',
                            height: `${Math.max(barH, 1)}%`,
                            background: isMarked ? 'rgba(34,197,94,0.6)' : 'rgba(34,197,94,0.35)',
                            borderTop: isMarked ? '2px solid #22c55e' : '1px solid #22c55e',
                          }}
                        />
                      ) : (
                        <div
                          className="absolute left-[15%] right-[15%]"
                          style={{
                            top: '50%',
                            height: `${Math.max(barH, 1)}%`,
                            background: isMarked ? 'rgba(239,68,68,0.6)' : 'rgba(239,68,68,0.35)',
                            borderBottom: isMarked ? '2px solid #ef4444' : '1px solid #ef4444',
                          }}
                        />
                      )}
                      {/* High price day label */}
                      {isHigh && (
                        <div
                          className={`absolute flex flex-col leading-none ${align}`}
                          style={{ bottom: `calc(50% + ${Math.max(barH, 16) + 6}%)` }}
                        >
                          <span className="text-green-500 text-[8px] font-bold">HIGH</span>
                          <span className="text-green-500 text-[7px] font-semibold tabular-nums mt-px">
                            {r.change >= 0 ? '+' : ''}{r.change.toFixed(1)}%
                          </span>
                        </div>
                      )}
                      {/* Low price day label */}
                      {isLow && (
                        <div
                          className={`absolute flex flex-col leading-none ${align}`}
                          style={{ top: `calc(50% + ${Math.max(barH, 16) + 6}%)` }}
                        >
                          <span className="text-red-500 text-[7px] font-semibold tabular-nums">
                            {r.change >= 0 ? '+' : ''}{r.change.toFixed(1)}%
                          </span>
                          <span className="text-red-500 text-[8px] font-bold mt-px">LOW</span>
                        </div>
                      )}
                    </div>
                  )
                })
              })()}
            </div>
          </div>
          {/* Weekly date ticks — always show full month */}
          <div className="flex items-center justify-between mt-4">
            {(() => {
              const daysInMonth = new Date(activeYear, effectiveMonth + 1, 0).getDate()
              const ticks: string[] = [`${MONTH_LABELS[effectiveMonth]} 1`]
              for (let d = 8; d <= daysInMonth; d += 7) {
                ticks.push(`${MONTH_LABELS[effectiveMonth]} ${d}`)
              }
              if (!ticks[ticks.length - 1]?.endsWith(`${daysInMonth}`)) {
                ticks.push(`${MONTH_LABELS[effectiveMonth]} ${daysInMonth}`)
              }
              return ticks.map((t, i) => (
                <span key={i} className="text-zinc-400 text-[9px] tabular-nums">{t}</span>
              ))
            })()}
          </div>
        </div>
      )}

      {/* Daily rows */}
      <div className="border border-zinc-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-800">
              <th className="text-left text-zinc-400 text-xs font-medium px-4 py-3 uppercase tracking-wider">
                Date
              </th>
              <th className="text-right text-zinc-400 text-xs font-medium px-4 py-3 uppercase tracking-wider">
                Price
              </th>
              <th className="text-right text-zinc-400 text-xs font-medium px-4 py-3 uppercase tracking-wider w-[160px] sm:w-[200px]">
                Change
              </th>
              <th className="text-right text-zinc-400 text-xs font-medium px-4 py-3 uppercase tracking-wider hidden sm:table-cell">
                Volume
              </th>
            </tr>
          </thead>
          <tbody>
            {monthRows.map((row) => {
              const isHigh = row.timestamp === highTs && highTs !== lowTs
              const isLow = row.timestamp === lowTs && highTs !== lowTs
              const barWidth = (Math.abs(row.change) / maxAbs) * 50

              return (
                <tr
                  key={row.timestamp}
                  className="border-b border-zinc-800/50 last:border-0"
                  style={{
                    backgroundColor: heatBg(row.change),
                    borderLeft: isHigh
                      ? '3px solid #22c55e'
                      : isLow
                        ? '3px solid #ef4444'
                        : '3px solid transparent',
                  }}
                >
                  <td className="px-4 py-3 text-zinc-300 text-sm whitespace-nowrap">{row.date}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <span className="text-white text-sm tabular-nums font-medium">
                      {formatPrice(row.price)}
                    </span>
                    {isHigh && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-green-500 border border-green-500/20 bg-green-500/[0.06] px-1.5 py-px ml-1.5">
                        HIGH
                      </span>
                    )}
                    {isLow && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-red-500 border border-red-500/20 bg-red-500/[0.06] px-1.5 py-px ml-1.5">
                        LOW
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <span
                        className={`text-sm tabular-nums ${
                          row.change >= 0 ? 'text-green-500' : 'text-red-500'
                        }`}
                      >
                        <span className="inline-flex items-center gap-1">
                          <svg
                            width="8"
                            height="8"
                            viewBox="0 0 8 8"
                            fill="currentColor"
                            className="flex-shrink-0"
                          >
                            {row.change >= 0 ? (
                              <path d="M4 1L7 5H1L4 1Z" />
                            ) : (
                              <path d="M4 7L1 3H7L4 7Z" />
                            )}
                          </svg>
                          {Math.abs(row.change).toFixed(2)}%
                        </span>
                      </span>
                      {/* Inline diverging bar */}
                      <div className="w-12 sm:w-16 h-2.5 relative flex-shrink-0">
                        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-zinc-800" />
                        {row.change >= 0 ? (
                          <div
                            className="absolute top-0 h-2.5"
                            style={{
                              left: '50%',
                              width: `${barWidth}%`,
                              background: 'rgba(34,197,94,0.3)',
                              borderRight: '1px solid #22c55e',
                            }}
                          />
                        ) : (
                          <div
                            className="absolute top-0 h-2.5"
                            style={{
                              right: '50%',
                              width: `${barWidth}%`,
                              background: 'rgba(239,68,68,0.3)',
                              borderLeft: '1px solid #ef4444',
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-400 text-sm text-right tabular-nums hidden sm:table-cell">
                    {formatVolume(row.volume)}
                  </td>
                </tr>
              )
            })}
            {monthRows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-zinc-400 text-sm text-center">
                  No data available for {MONTH_LABELS[effectiveMonth]} {activeYear}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Row count + Export */}
      {monthRows.length > 0 && (
        <div className="flex items-center justify-between mt-3">
          <p className="text-zinc-400 text-xs tabular-nums">
            {monthRows.length} days in {MONTH_LABELS[effectiveMonth]} {activeYear}
          </p>
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-1.5 text-zinc-400 text-xs border border-zinc-800 px-2.5 py-1.5 hover:text-white hover:border-zinc-600 transition-colors"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="flex-shrink-0"
            >
              <path d="M8 2V11M8 11L4.5 7.5M8 11L11.5 7.5M2 14H14" />
            </svg>
            Export CSV
          </button>
        </div>
      )}
    </div>
  )
}